import { useId, useRef, useState, type ChangeEvent, type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import { ReactComponent as CheckIcon } from '../../../../assets/icons/status-success.svg';
import { TRANSLATIONS_EXTERNAL_BENEFITS } from '../../../translations/commons';

import './DocumentSlot.scss';

const SLOT = TRANSLATIONS_EXTERNAL_BENEFITS.slot;

export interface DocumentSlotFile {
  id: string;
  name: string;
}

export interface DocumentSlotProps {
  title: string;
  description?: ReactNode;
  files: DocumentSlotFile[];
  sentFiles?: DocumentSlotFile[];
  accept?: string;
  multiple?: boolean;
  onAdd?: (files: File[]) => void;
  onRemoveSent?: (id: string) => void;
  onRemove?: (id: string) => void;
  error?: ReactNode;
  readOnly?: boolean;
}

const acceptedExtensions = (accept: string | undefined): string[] =>
  (accept ?? '')
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.startsWith('.'));

const hasExtension = (name: string, extensions: string[]): boolean =>
  extensions.some((ext) => name.toLowerCase().endsWith(ext));

const readExtensions = (extensions: string[]): string =>
  extensions.length < 2 ? extensions.join('') : `${extensions.slice(0, -1).join(', ')} or ${extensions.at(-1)}`;

const DocumentSlot = ({
  title,
  description,
  files,
  sentFiles = [],
  accept,
  multiple = false,
  onAdd,
  onRemove,
  onRemoveSent,
  error,
  readOnly = false
}: DocumentSlotProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejected, setRejected] = useState<string[]>([]);
  const titleId = useId();

  const accepts = !readOnly;
  const held = files.length + sentFiles.length;

  const stagedInline = !multiple && files.length > 0 ? files[0] : null;
  const sentInline = !multiple && files.length === 0 ? (sentFiles[0] ?? null) : null;
  const inlineFile = stagedInline ?? sentInline;
  const listFiles = multiple && held > 0;

  const removable = stagedInline !== null || (sentInline !== null && onRemoveSent !== undefined);
  const action = (() => {
    if (held === 0) return t(SLOT.attach);
    if (multiple) return t(SLOT.addMore);
    return removable ? t(SLOT.remove) : t(SLOT.replace);
  })();

  const extensions = acceptedExtensions(accept);
  const emit = (list: FileList | null) => {
    if (!accepts || !list?.length) return;
    const picked = multiple ? Array.from(list) : [list[0]];
    const allowed = extensions.length ? picked.filter((file) => hasExtension(file.name, extensions)) : picked;
    setRejected(picked.filter((file) => !allowed.includes(file)).map((file) => file.name));
    if (allowed.length) onAdd?.(allowed);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    emit(event.target.files);
    event.target.value = '';
  };

  const rejection = rejected.length
    ? t(SLOT.rejection, { names: rejected.join(', '), extensions: readExtensions(extensions) })
    : null;
  const problem = error ?? rejection;

  const className = ['document-slot', held > 0 ? 'document-slot--filled' : '', problem ? 'document-slot--error' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <li className={className}>
      <div className="document-slot__row">
        <div className="document-slot__text">
          <span className="document-slot__title" id={titleId}>
            {title}
          </span>
          {inlineFile ? (
            <span className="document-slot__attached">
              {!readOnly && stagedInline !== null && <CheckIcon width={14} height={14} />}
              <span className="document-slot__attached-name">{inlineFile.name}</span>
            </span>
          ) : (
            description && <span className="document-slot__description">{description}</span>
          )}
        </div>

        {accepts && (
          <button
            type="button"
            className={held > 0 ? 'document-slot__action document-slot__action--quiet' : 'document-slot__action'}
            onClick={() => {
              setRejected([]);
              if (!removable) {
                inputRef.current?.click();
                return;
              }
              if (stagedInline) onRemove?.(stagedInline.id);
              else if (sentInline) onRemoveSent?.(sentInline.id);
            }}
          >
            {action}
          </button>
        )}
      </div>

      {listFiles && (
        <ul className="document-slot__files">
          {sentFiles.map((file) => (
            <li key={file.id} className="document-slot__file document-slot__file--sent">
              <span className="document-slot__file-name">{file.name}</span>
              {!readOnly && onRemoveSent && (
                <button
                  type="button"
                  className="document-slot__file-remove"
                  onClick={() => onRemoveSent(file.id)}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon width={14} height={14} />
                </button>
              )}
            </li>
          ))}
          {files.map((file) => (
            <li key={file.id} className="document-slot__file">
              {!readOnly && <CheckIcon width={14} height={14} />}
              <span className="document-slot__file-name">{file.name}</span>
              {!readOnly && (
                <button
                  type="button"
                  className="document-slot__file-remove"
                  onClick={() => onRemove?.(file.id)}
                  aria-label={`Remove ${file.name}`}
                >
                  <CloseIcon width={14} height={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {problem && (
        <p className="document-slot__error" role="alert">
          {problem}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        aria-labelledby={titleId}
        className="document-slot__input"
        onChange={handleChange}
      />
    </li>
  );
};

export interface DocumentSlotListProps {
  children: ReactNode;
  framed?: boolean;
  className?: string;
}

export const DocumentSlotList = ({ children, framed = false, className }: DocumentSlotListProps) => (
  <ul
    className={['document-slot-list', framed ? 'document-slot-list--framed' : '', className].filter(Boolean).join(' ')}
  >
    {children}
  </ul>
);

export default DocumentSlot;
